import React , { useState, useEffect } from 'react';
import { array, bool, func, string, object } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import { isEmpty, has } from 'lodash';

import FormControl from '@material-ui/core/FormControl';
import AutoComplete from '../../components/FormFields/Autocomplete';

import EsmsDialog from '../../components/EsmsDialog';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as SiteActions from '../../actions/siteActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateEntityModal.module.css';

const CreateLinkModal = ({
    bobContacts,
    close,
    clearErrors,
    open,
    title,
    isLoading,
    errors,
    toggleLoading,
    linkType,
    erpAgents,
    erpClients,
    erpContacts,
    erpSites,
    selectedNode,
    clearErpEntities,
    ...props
}) => {

    const stateForInputType = {
        agent:   erpAgents,
        client:  erpClients,
        site:    erpSites,
        contact: erpContacts
    };
    const initialValues = {
        agent:       '',
        bobContacts: '',
        contact:     ''
    };

    const [bobContactsList, setBobContactsList] = useState([]);

    useEffect(() => {
        convertBobContactsList();
    }, [bobContacts]);

    const convertBobContactsList = () => {
        if (bobContacts) {
            const bobContactsFormatted = [
                {
                    items:     bobContacts,
                    name:      'BOB Contacts',
                    itemProps: { type: 'bobContacts' }
                }
            ];
            setBobContactsList(bobContactsFormatted);
        }
        else return {};

    };

    const helperText = (
        <div className={classes.labelColumn}>
            <div className={classes.labelHeader}>
                <img src="/assets/images/chain-flat.svg" alt="new area icon" />
                <span>Select ERP Contact</span>
            </div>
            <div className={classes.labelContent}>
                <p>
                    A Client Contact is representative of a person who works for a Client.
                </p>
                <p>
                    Select an ERP Agent.
                </p>
                <p>
                    Select an ERP Client where your Contacts reside.
                </p>
                <p>
                    Select an ERP Site (optional).
                </p>
                <p>
                    Select an ERP Contact to be linked to this Client Contact.
                </p>
            </div>
        </div>
    );
    const inputTypes = ['agent', 'client', 'site', 'contact'];
    const ValidationSchema = Yup.object().shape({
        agent:       Yup.string().required(),
        bobContacts: Yup.string().required(),
        contact:     Yup.string().required()
    });

    const formik = useFormik({
        initialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                createLink
            } = props;
            let isCreated;
            const body = {
                agentConfigurationId: values.contact.agentConfigurationId,
                erpEntityKind:        'contact',
                externalId:           values.contact.id,
                entityId:             values['bobContacts'].id
            };

            clearErrors();
            setSubmitting(true);
            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createLink(body);
                }
                if (isCreated) {
                    setSubmitting(false);
                    toggleLoading(false);

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        clearErpEntities();
        clearErrors();

        close();
    };

    const handleChange = async event => {
        const { value } = event.target;

        props.fetchErpDrilldown(value.type, value.id);

        if (value.type ==='agents') {
            formik.setFieldValue('client', null);
            formik.setFieldValue('site', null);
            formik.setFieldValue('contact', null);

        } else if (value.type === 'clients') {
            formik.setFieldValue('site', null);
            formik.setFieldValue('contact', null);
        } else if (value.type === 'sites') {
            formik.setFieldValue('contact', null);
        }

        formik.handleChange(event);
    };

    const onConfirm = () => {
        formik.handleSubmit();
    };

    const isAutoFillArrayEmpty = array => {
        if (!array || array.length < 1) {
            return true;
        }

        if (has(array[0], 'items')) {
            return isEmpty(array[0].items);
        }

        return true;
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={formik.handleReset}
            onConfirm={onConfirm}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Create"
        >
            <div className={classes.formColumn}>
                {helperText}
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        {inputTypes.map(inputType =>
                            <FormControl
                                className={classes.inputWrapper}
                                key={inputType}
                            >
                                <AutoComplete
                                    grouped
                                    label={inputType.charAt(0).toUpperCase() + inputType.slice(1)}
                                    name={inputType}
                                    classes={{
                                        inputRoot: classes.inputRoot
                                    }}
                                    className={classes.autoComplete}
                                    onChange={handleChange}
                                    onBlur={formik.handleBlur}
                                    options={stateForInputType[inputType]}
                                    disabled={isAutoFillArrayEmpty(stateForInputType[inputType])}
                                    value={formik.values[inputType]}
                                />
                            </FormControl>
                        )}
                        <FormControl
                            className={classes.inputWrapper}
                            key="bobContacts"
                        >
                            <AutoComplete
                                grouped
                                label="BOB Contacts"
                                name="bobContacts"
                                classes={{
                                    inputRoot: classes.inputRoot
                                }}
                                className={classes.autoComplete}
                                onChange={handleChange}
                                onBlur={formik.handleBlur}
                                options={bobContactsList}
                                value={formik.values['bobContacts']}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

CreateLinkModal.defaultProps = {
    erpContacts: []
};

CreateLinkModal.propTypes = {
    erpContacts:      array,
    erpClients:       array,
    erpSites:         array,
    erpAgents:        array,
    close:            func.isRequired,
    open:             bool.isRequired,
    title:            string.isRequired,
    linkType:         string,
    selectedNode:     object,
    clearErpEntities: func
};

const mapStateToProps = state => {
    return {
        bobContacts:  state.architect.contacts,
        errors:       state.messages.errors,
        isLoading:    state.ui.isLoading,
        erpContacts:  state.architect.erpContacts,
        erpAgents:    state.architect.erpAgents,
        erpClients:   state.architect.erpClients,
        erpSites:     state.architect.erpSites,
        selectedNode: state.architect.selectedNode,
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...SiteActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateLinkModal);
